import React from 'react';

/**
 * Skeleton placeholder primitives.
 *
 * Use these instead of a bare "Loading..." string to give the user a sense of
 * the layout they are waiting for. All variants are pure CSS — no animations
 * library required.
 *
 *   <SkeletonText width="60%" />
 *   <SkeletonTable rows={5} cols={4} />
 *   <SkeletonCard />
 */

const baseShimmer = {
  background: 'linear-gradient(90deg, #f3f4f6 25%, #e5e7eb 37%, #f3f4f6 63%)',
  backgroundSize: '400% 100%',
  animation: 'lo-skeleton 1.4s ease infinite',
  borderRadius: 4,
};

// Inject the shimmer keyframes once.
let stylesInjected = false;
function ensureStyles() {
  if (stylesInjected || typeof document === 'undefined') return;
  const style = document.createElement('style');
  style.setAttribute('data-skeleton-styles', 'true');
  style.innerHTML = `@keyframes lo-skeleton { 0% { background-position: 100% 50% } 100% { background-position: 0 50% } }`;
  document.head.appendChild(style);
  stylesInjected = true;
}

export function SkeletonText({ width = '100%', height = 14, style = {} }) {
  ensureStyles();
  return (
    <span
      aria-hidden="true"
      style={{ display: 'inline-block', width, height, ...baseShimmer, ...style }}
    />
  );
}

export function SkeletonTable({ rows = 5, cols = 4, ariaLabel = 'Loading table data' }) {
  ensureStyles();
  return (
    <div role="status" aria-live="polite" aria-label={ariaLabel} style={{ width: '100%' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            {Array.from({ length: cols }).map((_, i) => (
              <th key={i} scope="col" style={{ padding: 8 }}>
                <SkeletonText width="70%" height={12} />
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: rows }).map((_, r) => (
            <tr key={r}>
              {Array.from({ length: cols }).map((_, c) => (
                <td key={c} style={{ padding: 8 }}>
                  <SkeletonText width={`${60 + ((r * c) % 30)}%`} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function SkeletonCard({ lines = 3, ariaLabel = 'Loading' }) {
  ensureStyles();
  return (
    <div
      role="status"
      aria-live="polite"
      aria-label={ariaLabel}
      style={{ padding: 16, border: '1px solid #e5e7eb', borderRadius: 8, background: '#fff' }}
    >
      <SkeletonText width="40%" height={18} style={{ marginBottom: 12 }} />
      {Array.from({ length: lines }).map((_, i) => (
        <div key={i} style={{ marginBottom: 8 }}>
          <SkeletonText width={`${70 - i * 10}%`} />
        </div>
      ))}
    </div>
  );
}

export default SkeletonTable;
